import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { requestNavigation } from '../utils/navigationGuard';
import { ProductIcon, productTint } from './productIcons';
import './ProductSidebar.css';

/**
 * Launchpad — fixed left rail giving one-click access to every product page.
 * Collapsed state shows all 10 product icons stacked (no extra click needed
 * to see what's available). Expands on hover to reveal labels, grouped by
 * category. Click a row to navigate (or open the auth modal if logged out).
 *
 * Icons and accent colours come from `productIcons.jsx` (shared with the
 * homepage and the Chrome extension's side panel) so a tool looks the same
 * everywhere. Only the label is local — this rail says "AI Certification"
 * where the homepage says "Course Creator".
 */

const GROUPS = [
  {
    label: 'Practice',
    items: [
      { label: 'Interview Assistant', path: '/InterviewAssistant' },
      { label: 'Mock Interview',      path: '/mockInterview' },
      { label: 'Interview Questions', path: '/InterviewQuestions' },
    ],
  },
  {
    label: 'Build',
    items: [
      { label: 'Resume Builder',     path: '/resume-builder' },
      { label: 'Cover Letter',       path: '/CoverLetter' },
      { label: 'Portfolio Builder',  path: '/portfolio' },
    ],
  },
  {
    label: 'Optimize',
    items: [
      { label: 'ATS Check',          path: '/ATSCheck' },
      { label: 'LinkedIn Optimizer', path: '/LinkedInOptimizer' },
    ],
  },
  {
    label: 'Apply & Learn',
    items: [
      { label: 'Auto Apply',        path: '/AutoJobApply' },
      { label: 'AI Certification',  path: '/certification' },
    ],
  },
];

const ProductSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Launchpad tiles take everyone — including signed-out visitors — straight
  // to the product's landing page. The landing pages handle their own auth
  // prompts at the moment of an action that actually requires an account
  // (e.g. starting a session, deducting credits). Gating the rail itself was
  // a deterrent: visitors couldn't even browse what the product does.
  const handleClick = (path) => {
    // Route through the global guard so the live-interview page can pop
    // its exit-confirmation modal before any navigation actually happens.
    // When no guard is installed, this just calls navigate(path) directly.
    requestNavigation(path, (target) => {
      navigate(target);
      window.scrollTo(0, 0);
    });
  };

  return (
    <aside className="launchpad" aria-label="Product launchpad">
      <div className="lp-rail">
          <div className="lp-brand">
            <img
              className="lp-brand-logo"
              src={`${process.env.PUBLIC_URL || ''}/android-chrome-192x192.png`}
              alt=""
              width="24"
              height="24"
              aria-hidden="true"
            />
            <span className="lp-brand-text">Terview Launchpad</span>
          </div>

          {GROUPS.map((group) => (
            <div className="lp-group" key={group.label}>
              <div className="lp-group-label">{group.label}</div>
              <div className="lp-group-items">
                {group.items.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      type="button"
                      className={`lp-item ${active ? 'is-active' : ''}`}
                      onClick={() => handleClick(item.path)}
                      style={{ '--lp-accent': productTint(item.path) }}
                      title={item.label}
                    >
                      <span className="lp-item-icon" aria-hidden="true">
                        <ProductIcon path={item.path} size={18} />
                      </span>
                      <span className="lp-item-label">{item.label}</span>
                      {active && <span className="lp-active-bar" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="lp-foot">
            <span className="lp-foot-dot" aria-hidden="true" />
            <span className="lp-foot-text">TERVIEW.AI</span>
          </div>
        </div>
    </aside>
  );
};

export default ProductSidebar;
